import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";

interface InstitutionAdminData {
  institutionId: string;
  institutionName: string;
  institutionSlug: string;
}

const SELECTED_INSTITUTION_KEY = "careerfy_selected_institution_id";

export function useInstitutionAdmin() {
  const { user, isLoading: authLoading } = useAuth();
  const [selectedInstitutionId, setSelectedInstitutionId] = useState<string | null>(() => {
    // Initialize from localStorage
    if (typeof window !== "undefined") {
      return localStorage.getItem(SELECTED_INSTITUTION_KEY);
    }
    return null;
  });

  // Fetch ALL institutions the user is admin of
  const { data: allInstitutions, isLoading: queryLoading, error } = useQuery({
    queryKey: ["institution-admin-all", user?.id],
    queryFn: async (): Promise<InstitutionAdminData[]> => {
      if (!user) return [];

      const { data: adminRows, error: adminError } = await supabase
        .from("institution_admins")
        .select(`
          institution_id,
          institutions (
            id,
            name,
            slug
          )
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (adminError) {
        console.error("Error checking institution admin status:", adminError);
        return [];
      }

      if (!adminRows || adminRows.length === 0) return [];

      return adminRows
        .filter((row) => (row as any).institutions)
        .map((row) => {
          const institution = (row as any).institutions as {
            id: string;
            name: string;
            slug: string;
          };
          return {
            institutionId: institution.id,
            institutionName: institution.name,
            institutionSlug: institution.slug,
          };
        });
    },
    enabled: !!user,
    refetchOnMount: true,
    staleTime: 0,
  });

  // Determine the currently selected institution
  const currentInstitution = (() => {
    if (!allInstitutions || allInstitutions.length === 0) return null;

    // If there's a selected ID, try to find it
    if (selectedInstitutionId) {
      const found = allInstitutions.find(
        (inst) => inst.institutionId === selectedInstitutionId
      );
      if (found) return found;
    }

    // Default to the first institution
    return allInstitutions[0];
  })();

  // Update localStorage when selection changes
  useEffect(() => {
    if (currentInstitution?.institutionId) {
      localStorage.setItem(SELECTED_INSTITUTION_KEY, currentInstitution.institutionId);
    }
  }, [currentInstitution?.institutionId]);

  const selectInstitution = (institutionId: string) => {
    setSelectedInstitutionId(institutionId);
    localStorage.setItem(SELECTED_INSTITUTION_KEY, institutionId);
  };

  // CRITICAL: isLoading must be true while auth is loading OR while the query is loading
  const isLoading = authLoading || (!!user && queryLoading);

  return {
    isInstitutionAdmin: (allInstitutions?.length ?? 0) > 0,
    institutionData: currentInstitution,
    allInstitutions: allInstitutions ?? [],
    hasMultipleInstitutions: (allInstitutions?.length ?? 0) > 1,
    selectInstitution,
    isLoading,
    error,
  };
}
